import type { UserSiteRole } from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@lms/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@lms/ui/popover';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@lms/ui/sidebar';
import { cn } from '@lms/ui/utils';
import {
  AreaChart,
  AtSign,
  Building,
  Calendar,
  Check,
  ChevronsUpDown,
  GraduationCap,
  Handshake,
  LayoutTemplateIcon,
  LogOut,
  MessageCircleQuestionIcon,
  QrCode,
  ScrollText,
  Settings,
  ShoppingBag,
  SparklesIcon,
  UserCircle,
  UserSearch,
  Users,
  ZapIcon,
} from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslations } from 'use-intl';
import { type SiteAccess, useCurrentUser } from '@/features/auth/use-auth';
import type { Site } from '@/features/branding/brand-provider';
import { LanguageSelector } from '@/features/locale/components/language-selector';

// Site switcher. Inlined port of features/branding/components/slug-selector.tsx
// (the Next app kept it in its own file; here it is private to the header).
const SlugSelector: React.FC<{
  slugs: {
    siteSlug: string;
    siteName: string;
    portfolioSlug: string | null;
  }[];
  activeSlug: string;
  onChange: (slug: string) => void;
  className?: string;
  variant?: 'default' | 'folded';
}> = ({ slugs, activeSlug, onChange, className, variant }) => {
  const [open, setOpen] = useState(false);
  const t = useTranslations('admin');

  const topLevel = slugs
    .filter((s) => s.portfolioSlug === null)
    .sort((a, b) => a.siteName.localeCompare(b.siteName));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            variant !== 'folded'
              ? 'w-full justify-between overflow-clip'
              : 'w-4 justify-center overflow-hidden p-2',
            className,
          )}
        >
          {variant !== 'folded' &&
            slugs.find((s) => s.siteSlug === activeSlug)?.siteName}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-full p-0', className)}>
        <Command>
          <CommandInput placeholder={t('search_site')} />
          <CommandList className="max-h-[80dvh]">
            <CommandEmpty />
            {topLevel.map((tl) => (
              <CommandGroup key={tl.siteSlug} heading={tl.siteName}>
                <CommandItem
                  value={tl.siteSlug}
                  onSelect={(currentValue) => {
                    if (currentValue !== activeSlug) {
                      onChange(currentValue);
                    }
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      activeSlug === tl.siteSlug ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  {tl.siteName}
                </CommandItem>
                {slugs
                  .filter((s) => s.portfolioSlug === tl.siteSlug)
                  .map((s) => (
                    <CommandItem
                      key={s.siteSlug}
                      value={s.siteSlug}
                      onSelect={(currentValue) => {
                        if (currentValue !== activeSlug) {
                          onChange(currentValue);
                        }
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          activeSlug === s.siteSlug
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                      {s.siteName}
                    </CommandItem>
                  ))}
              </CommandGroup>
            ))}
            {topLevel.length === 0 && (
              <CommandGroup>
                {slugs.map((s) => (
                  <CommandItem
                    key={s.siteSlug}
                    value={s.siteSlug}
                    onSelect={(currentValue) => {
                      if (currentValue !== activeSlug) {
                        onChange(currentValue);
                      }
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        activeSlug === s.siteSlug ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {s.siteName}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
SlugSelector.displayName = 'SlugSelector';

// Ports app/[brand]/admin/header.tsx: the role-gated sidebar navigation, brand
// logo, user footer, language + site switchers, and logout.
export function AdminHeader({
  site,
  access,
  slugs,
  activeUser,
}: {
  site: Site;
  access: SiteAccess | undefined;
  slugs: UserSiteRole[];
  activeUser: { name: string };
}) {
  const navigate = useNavigate();
  const t = useTranslations();
  const location = useLocation();
  const { open } = useSidebar();
  const { refresh } = useCurrentUser();

  const isActive = (link: string, exact = false) =>
    (exact && location.pathname === `/${site.slug}${link}`) ||
    (!exact && location.pathname.startsWith(`/${site.slug}${link}`));

  const onLogout = () => {
    // The Next app hit /api/logout to clear the HttpOnly session cookie. There
    // is no backend logout endpoint yet, so send the user to login and let SWR
    // re-validate `getMe()` (a 401 clears the cached user).
    navigate(`/${site.slug}/login`);
    refresh();
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="mt-8 mb-4">
        <Link
          to={`/${site.slug}/admin`}
          className="flex items-center gap-2 text-lg font-semibold md:text-base"
        >
          <img
            src={site.logo}
            className="mx-auto h-full max-h-24 w-full max-w-32 object-scale-down transition-all"
            alt={site.name}
          />
          <span className="sr-only">{site.name}</span>
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton isActive={isActive('/admin', true)} asChild>
                  <Link to={`/${site.slug}/admin`}>
                    <AreaChart className="h-4 w-4" />
                    {t('admin.dashboard')}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {access && site.siteType === 'location' && (
                <>
                  {access.canManageGroups && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={isActive('/admin/codes')}
                        asChild
                      >
                        <Link to={`/${site.slug}/admin/codes`}>
                          <QrCode className="h-4 w-4" />
                          {t('admin.codes')}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  {site.hasGroups && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={isActive('/admin/groups')}
                        asChild
                      >
                        <Link to={`/${site.slug}/admin/groups`}>
                          {site.groupAlias === 'stores' ? (
                            <ShoppingBag className="h-4 w-4" />
                          ) : site.groupAlias === 'events' ? (
                            <Calendar className="h-4 w-4" />
                          ) : (
                            <Users className="h-4 w-4" />
                          )}
                          {t(`admin.${site.groupAlias}`)} &amp;{' '}
                          {t('admin.suppliers')}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={isActive('/admin/curriculums')}
                      asChild
                    >
                      <Link to={`/${site.slug}/admin/curriculums`}>
                        <ScrollText className="h-4 w-4" />
                        {t('admin.curriculums')}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={isActive('/admin/courses')}
                      asChild
                    >
                      <Link to={`/${site.slug}/admin/courses`}>
                        <GraduationCap className="h-4 w-4" />
                        {t('admin.courses')}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </>
              )}
              {site.siteType === 'portfolio' && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={isActive('/admin/locations')}
                    asChild
                  >
                    <Link to={`/${site.slug}/admin/locations`}>
                      <Building className="h-4 w-4" />
                      {t('admin.locations')}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {access?.canManageUsers && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={isActive('/admin/users')}
                    asChild
                  >
                    <Link to={`/${site.slug}/admin/users`}>
                      <UserSearch className="h-4 w-4" />
                      {t('admin.users')}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {access?.superAdmin && (
          <>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarGroupLabel>
                  {t('admin.site-administration')}
                </SidebarGroupLabel>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={isActive('/admin/settings')}
                      asChild
                    >
                      <Link to={`/${site.slug}/admin/settings`}>
                        <Settings className="h-4 w-4" />
                        {t('admin.settings')}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={isActive('/admin/email')}
                      asChild
                    >
                      <Link to={`/${site.slug}/admin/email`}>
                        <AtSign className="h-4 w-4" />
                        {t('admin.email_templates')}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarGroupLabel>{t('roles.super-admin')}</SidebarGroupLabel>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={isActive('/admin/superstats')}
                      asChild
                    >
                      <Link to={`/${site.slug}/admin/superstats`}>
                        <ZapIcon className="h-4 w-4" />
                        {t('admin.superstats')}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={isActive('/admin/demo')}
                      asChild
                    >
                      <Link to={`/${site.slug}/admin/demo`}>
                        <SparklesIcon className="h-4 w-4" />
                        {t('demo.title')}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={isActive('/admin/portfolios')}
                      asChild
                    >
                      <Link to={`/${site.slug}/admin/portfolios`}>
                        <Handshake className="h-4 w-4" />
                        {t('admin.portfolios')}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={isActive('/admin/templates')}
                      asChild
                    >
                      <Link to={`/${site.slug}/admin/templates`}>
                        <LayoutTemplateIcon className="h-4 w-4" />
                        {t('admin.templates')}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={isActive('/admin/surveys')}
                      asChild
                    >
                      <Link to={`/${site.slug}/admin/surveys`}>
                        <MessageCircleQuestionIcon className="h-4 w-4" />
                        {t('admin.surveys')}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
      </SidebarContent>
      <SidebarFooter className="mb-2 gap-4 px-6 group-data-[collapsible=icon]:p-2">
        <div className="flex flex-row items-center justify-start gap-2 group-data-[collapsible=icon]:justify-center">
          <UserCircle className="h-6 w-6" />
          <div className="flex-grow group-data-[collapsible=icon]:hidden">
            <div>{activeUser.name}</div>
            <div className="text-muted-foreground text-sm">
              {access?.superAdmin && t('roles.super-admin')}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onLogout}
            className="group-data-[collapsible=icon]:hidden"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
        <LanguageSelector
          className="w-full"
          variant={!open ? 'folded' : 'default'}
        />
        <SlugSelector
          className="bg-muted w-full"
          activeSlug={site.slug}
          slugs={slugs}
          onChange={(slug) => navigate(`/${slug}/admin`)}
          variant={!open ? 'folded' : 'default'}
        />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
